import React, {Fragment} from "react";
import {NextPage} from "next";
import shopApi from "../api/shop";
import {domainUrl} from "../helper";
import {useRouter} from "next/router";
import Head from "next/head";
import MainLayout, {ILayout} from "../components/ForCWV/Wrappers/MainLayout";
import {SignPageTranslations} from "../services/translationKeys";
import {layoutProps} from "../services/layoutProps";
import {TranslationsType} from "../types/homePageTypes";
import AccountPageLogin from "../components/account/AccountPageLogin";
import ReCaptchaWrapper from "../components/shared/ReCaptchaWrapper";

interface IProps extends ILayout {
    translationsData: TranslationsType;
    selectedLocale: string,
    dbName: string
    breadcrumb: Array<{ title: string; url: string }>;
    customFieldGenerate: any;
    recaptchaActive: string;
    recaptchaKey: string
}

const Signup: NextPage<IProps> = (pageProps) => {
    const {
        dbName,
        selectedLocale,
        translationsData,
        breadcrumb,
        customFieldGenerate,
        recaptchaActive,
        recaptchaKey,
        defaultLangCode,
        locales
    } = pageProps
    const router = useRouter()
    const meta_title = translationsData.SignUp
    const meta_description = "Sign Up page"
    const meta_keywords = ""
    const meta_url = domainUrl(`${dbName}${router.locale !== "catchAll" && router.locale !== defaultLangCode ? "/" + selectedLocale : ""}${router.asPath}`)

    return (
        <ReCaptchaWrapper recaptchaActive={recaptchaActive} recaptchaKey={recaptchaKey} >
            <MainLayout {...pageProps}>
                <Fragment>
                    <Head>
                        <title>{meta_title}</title>
                        <meta name="title" content={meta_title}/>
                        <meta name="description" content={meta_description}/>
                        <meta name="keywords" content={meta_keywords}/>
                        <meta name="generator" content="Zegashop.com"/>
                        <meta name="twitter:description" content={meta_description}/>
                        <meta name="twitter:title" content={meta_title}/>
                        <meta property="og:description" content={meta_description}/>
                        <meta property="og:title" content={meta_title}/>
                        <meta property="og:keywords" content={meta_keywords}/>
                        <meta property="og:url" content={meta_url}/>
                        <meta property="og:locale" content={selectedLocale}/>
                        <link rel="canonical" href={meta_url}/>
                        {locales?.length > 0 ? (
                            <>
                                <link rel="alternate" href={domainUrl(`${dbName}${router.asPath}`)} hrefLang="x-default"/>
                                <link rel="alternate" href={domainUrl(`${dbName}${router.asPath}`)} hrefLang={defaultLangCode}/>
                                {locales.map(intl => (
                                    <link rel="alternate" href={domainUrl(`${dbName}/${intl.code}${router.asPath}`)} hrefLang={intl.code}/>
                                ))}
                            </>
                        ) : null}
                    </Head>
                    <AccountPageLogin
                        dbName={dbName}
                        breadcrumb={breadcrumb}
                        customFieldGenerate={customFieldGenerate}
                        translationsData={translationsData}
                        recaptchaActive={recaptchaActive}
                    />
                </Fragment>
            </MainLayout>
        </ReCaptchaWrapper>
    )
}

export async function getServerSideProps({locale, req, res, query}: any) {
    const typedReq = req as unknown as {
        m: { [key: string]: any }[],
        defaultDB: { db_name: string },
        assets: { logo_image: string },
        ipv4: string
    }
    const wrapperProps = await layoutProps({
        requestHeaders: req.headers,
        requestDbName: typedReq.defaultDB.db_name,
        requestModels: typedReq.m,
        requestLogo: typedReq.assets.logo_image,
        nextJSLocale: locale,
        translationsObject: SignPageTranslations,
        ipv4: typedReq.ipv4,
    })
    const { maintenanceInfo, suspendStatus, selectedLocale, translationsData: {Home: HomeIntl, SignUp: SignUpIntl} } = wrapperProps
    if (String(suspendStatus) === "469") {
        return {
            redirect: { destination: '/suspend', permanent: false },
        }
    }
    if (maintenanceInfo?.is_maintenance_on) {
        return {
            redirect: { destination: '/maintenance', permanent: false },
        }
    }
    const breadcrumb = [
        {title: HomeIntl, url: `/`},
        {title: SignUpIntl, url: ``}
    ]

    const getFields = await shopApi.getFields(selectedLocale, req.m);
    const customFieldGenerate = getFields.map((item: any) => {
        if (item.display_form.split(",").some((item: string) => item === "registration_form")) {
            return item
        }
    }).sort((a: { position: string }, b: { position: string }) => +a.position - +b.position)

    return {
        props: {
            breadcrumb,
            locale: selectedLocale,
            customFieldGenerate,
            ...wrapperProps,
        },
    };
}

export default Signup;
